import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingAccountLayout from 'pages/booking/account/BookingAccountLayout';
import {
  AccountError,
  AccountSkeleton,
  BookingInterval,
  PeriodicBookingBadge,
  ReservationStatusBadge,
  clearBookingDraft,
  openCalendarBlob,
} from 'pages/booking/account/account-utils';
import { BOOKING_ACCOUNT_INVOICE_DETAIL_PATH, BOOKING_DETAIL_PATH } from 'routes/paths';
import useLang from 'lang';
import { formatLocaleDateTime } from 'lang/date-time';

const ACTION_REASON_KEYS: Record<string, string> = {
  'reservation-ended': 'The reservation has already ended.',
  'paid-reservation': 'Paid reservations cannot be cancelled online.',
  'reservation-state': 'This reservation state does not allow cancellation.',
  'guest-cancellation-disabled': 'Guest reservations cannot be cancelled online.',
  'space-unavailable': 'This space is no longer available for booking.',
  'reservation-not-accepted': 'Calendar events are available after acceptance.',
  'payment-not-resumable': 'This payment can no longer be resumed.',
  'invoice-unavailable': 'The invoice is not available yet.',
  unavailable: 'This action is currently unavailable.',
  'periodic-booking-managed-by-operator': 'Periodic bookings are managed by your workspace operator.',
};

/**
 * Complete owner-authorized reservation detail and its currently valid actions.
 */
export default function AccountBookingDetail() {
  const { reservationId } = useParams<{ reservationId: string }>();
  const { getAction } = useApi();
  const { _t, lang } = useLang();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = (await getAction('GetReservationDetail')({
        parameters: { reservationId: Number(reservationId) },
      })) as any;
      setReservation(data.reservation);
    } catch (_error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [getAction, reservationId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Cancels through the existing lifecycle command and refreshes capabilities.
   */
  const cancel = async () => {
    if (!window.confirm(_t('Are you sure you want to cancel this reservation?'))) return;
    setBusy(true);
    try {
      await getAction('CancelReservation')({ parameters: { id: Number(reservationId) } });
      await load();
    } finally {
      setBusy(false);
    }
  };

  /**
   * Fetches the privacy-safe calendar event.
   */
  const addToCalendar = async () => {
    const blob = await getAction('GetReservationCalendar')({
      parameters: { reservationId: Number(reservationId) },
    });
    openCalendarBlob(blob, Number(reservationId));
  };

  return (
    <BookingAccountLayout>
      {loading && <AccountSkeleton />}
      {error && <AccountError retry={() => void load()} />}
      {reservation && (
        <article className="account-detail-card">
          <div className="account-card-heading">
            <div>
              <p className="account-eyebrow">
                {_t('Reservation')} #{reservation.id}
              </p>
              <h2>{reservation.space.name}</h2>
              <p>{reservation.building.title}</p>
            </div>
            <ReservationStatusBadge status={reservation.status} />
          </div>
          <dl className="account-detail-list">
            <div>
              <dt>{_t('Date and time')}</dt>
              <dd>
                <BookingInterval startsAt={reservation.startsAt} endsAt={reservation.endsAt} />
              </dd>
            </div>
            <div>
              <dt>{_t('People')}</dt>
              <dd>{reservation.personsCount}</dd>
            </div>
            <div>
              <dt>{_t('Created')}</dt>
              <dd>{formatLocaleDateTime(reservation.createdAt, lang)}</dd>
            </div>
            {reservation.note && (
              <div>
                <dt>{_t('Note')}</dt>
                <dd>{reservation.note}</dd>
              </div>
            )}
          </dl>
          <PeriodicBookingBadge periodic={reservation.periodic} />
          {reservation.periodic && (
            <section className="account-finance-card">
              <h3>{_t('Periodic booking')}</h3>
              <p>{_t('This booking is managed by your workspace operator and cannot be changed or cancelled here.')}</p>
            </section>
          )}
          {reservation.status === 'accepted' && new Date(reservation.endsAt) > new Date() && (
            <section className="account-finance-card">
              <h3>{_t('Access')}</h3>
              <p>
                {_t(
                  'Use your normal Phillip access method, such as your RFID card or PIN, during the reservation period.'
                )}
              </p>
            </section>
          )}
          {reservation.payment && (
            <section className="account-finance-card">
              <h3>{_t('Payment')}</h3>
              <p>
                {_t('Total')}:{' '}
                <strong>
                  {Number(reservation.payment.amountWithVat).toFixed(2)} {_t('EUR')}
                </strong>
              </p>
              {reservation.payment.invoice?.payBySquareQr && (
                <img className="account-qr" src={reservation.payment.invoice.payBySquareQr} alt={_t('PAY by square')} />
              )}
              {reservation.payment.invoice?.iban && (
                <p>
                  {_t('IBAN')}: {reservation.payment.invoice.iban}
                </p>
              )}
              {reservation.payment.invoice?.variableSymbol && (
                <p>
                  {_t('Variable symbol')}: {reservation.payment.invoice.variableSymbol}
                </p>
              )}
            </section>
          )}
          <div className="account-actions">
            {reservation.actions.canCancel ? (
              <button className="account-button account-button--danger" disabled={busy} onClick={cancel}>
                {_t('Cancel reservation')}
              </button>
            ) : (
              <p className="account-action-reason">
                {_t(
                  ACTION_REASON_KEYS[reservation.actions.cancelUnavailableReason] ||
                    'This reservation cannot be cancelled.'
                )}
              </p>
            )}
            {reservation.actions.canBookAgain && (
              <button
                className="account-button account-button--secondary"
                onClick={() => {
                  clearBookingDraft();
                  navigate(BOOKING_DETAIL_PATH.insert({ slug: reservation.space.slug }));
                }}
              >
                {_t('Book again')}
              </button>
            )}
            {reservation.actions.canAddToCalendar && (
              <button className="account-button account-button--secondary" onClick={() => void addToCalendar()}>
                {_t('Add to calendar')}
              </button>
            )}
            {reservation.actions.canResumePayment && (
              <button
                className="account-button"
                onClick={() => window.location.assign(reservation.payment.attempt.checkoutUrl)}
              >
                {_t('Continue to payment')}
              </button>
            )}
            {reservation.actions.canPreviewInvoice && reservation.payment?.invoice?.id && (
              <button
                className="account-button account-button--secondary"
                onClick={() =>
                  navigate(
                    BOOKING_ACCOUNT_INVOICE_DETAIL_PATH.insert({
                      invoiceId: String(reservation.payment.invoice.id),
                    })
                  )
                }
              >
                {_t('View invoice')}
              </button>
            )}
          </div>
        </article>
      )}
    </BookingAccountLayout>
  );
}
